import { Injectable } from '@angular/core';
import { Response, URLSearchParams, RequestOptions } from '@angular/http';
import { Observable } from 'rxjs/Observable';

import { SurveyInformationMapper } from './fill-out-the-questionnaire/survey-information.mapper';
import { Answer } from './fill-out-the-questionnaire/answer.interface';
import { SurveyInformation } from './fill-out-the-questionnaire/survey-information.model';
import { AuthHttpService } from 'app/security/authorization';
import { PaginationList } from 'app/shared';
import { environment } from 'environments/environment';
import { Survey, SurveyId } from './survey.model';
import { SurveyMapper } from './survey.mapper';
import { QuestionAdapter } from 'app/question/question.adapter';
import { SessionId } from 'app/session/session.model';
import { UserId } from 'app/user';
import { PAGE } from './fill-out-the-questionnaire/page.const';

@Injectable()
export class SurveyRepository {

  constructor(
    private authHttp: AuthHttpService
  ) { }

  public list(params?: URLSearchParams): Observable<PaginationList<Survey>> {
    return this.authHttp.get(
        environment.apiUrl + 'survey',
        { search: params }
      )
      .map(response => response.json())
      .map((list): PaginationList<Survey> => new PaginationList<Survey>(
          list.items.map(SurveyMapper.map),
          list.total
        )
      );
  }

  public autocompleteSurveySession(name: string, type: string): Observable<Survey[]> {
    const params: URLSearchParams = new URLSearchParams();
    params.set('filters[name]', name);
    params.set('filters[type]', type);
    params.set('limit', '1000');

    const requestOptions: RequestOptions = new RequestOptions();
    requestOptions.search = params;

    return this.authHttp.get(
        environment.apiUrl + 'survey',
        requestOptions
      )
      .map(response => response.json())
      .map((list: any): Survey[] => list.items.map(SurveyMapper.map));
  }

  public get(surveyId: SurveyId): Observable<Survey> {
    return this.authHttp.get(
        environment.apiUrl + 'survey/' + surveyId
      )
      .map(response => response.json())
      .map(SurveyMapper.map);
  }

  public create(
    name: string,
    type: string,
    randomizedQuestions: boolean,
    reanswerEnabled: boolean,
    description: string,
    questionsPerPage: number
  ): Observable<{ id: SurveyId }> {
    return this.authHttp.post(
        environment.apiUrl + 'survey',
        {
          name,
          type,
          randomizedQuestions,
          reanswerEnabled,
          description,
          questionsPerPage
        }
      )
      .map(response => response.json());
  }

  public update(
    surveyId: SurveyId,
    name: string,
    type: string,
    randomizedQuestions: boolean,
    reanswerEnabled: boolean,
    description: string,
    questionsPerPage: number
  ) {
    return this.authHttp.put(
        environment.apiUrl + 'survey/' + surveyId,
        {
          name,
          type,
          randomizedQuestions,
          reanswerEnabled,
          description,
          questionsPerPage
        }
      );
  }

  public delete(id: SurveyId) {
    return this.authHttp.delete(
      environment.apiUrl + 'survey/' + id
    );
  }

  public duplicate(id: SurveyId): Observable<{ id: SurveyId }> {
    return this.authHttp.post(
      environment.apiUrl + 'survey/' + id + '/duplicate',
      null
    )
    .map(response => response.json());
  }

  public getSurveyPreview(id: SurveyId, params?: URLSearchParams): Observable<PaginationList<any>> {
    return this.authHttp.get(
      environment.apiUrl + 'survey/' + id + '/preview',
      { search: params }
    )
    .map(response => response.json())
    .map((list: any): PaginationList<any> => new PaginationList<any>(
        list.items.map(QuestionAdapter.map),
        list.total,
        list.limit
      )
    )
  }

  public getParticipantQuestionsList(
    surveyId: SurveyId,
    sessionId: SessionId,
    participantId: UserId,
    params?: URLSearchParams
  ): Observable<PaginationList<any>> {
    return this.authHttp.get(
        environment.apiUrl + 'participant/' + participantId + '/session/' + sessionId + '/survey/' + surveyId + '/questions',
        { search: params }
      )
      .map(response => response.json())
      .map((list: any): PaginationList<any> => new PaginationList<any>(
          list.items.map(QuestionAdapter.map),
          list.total,
          list.limit
        )
      );
  }

  public getParticipantAnswersList(
    surveyId: SurveyId,
    sessionId: SessionId,
    participantId: UserId,
    params?: URLSearchParams
  ): Observable<PaginationList<any>> {
    return this.authHttp.get(
        environment.apiUrl + 'participant/' + participantId + '/session/' + sessionId + '/survey/' + surveyId + '/preview',
        { search: params }
      )
      .map(response => response.json())
      .map((list: any): PaginationList<any> => new PaginationList<any>(
          list.items.map(QuestionAdapter.map),
          list.total,
          list.limit
        )
      );
  }

  public getSurveyInformation(
    surveyId: SurveyId,
    sessionId: SessionId,
    participantId: UserId
  ): Observable<SurveyInformation> {
    return this.authHttp.get(
        environment.apiUrl + 'participant/' + participantId + '/session/' + sessionId + '/survey/' + surveyId
      )
      .map(response => response.json())
      .map(SurveyInformationMapper.map);
  }

  public startSurvey(
    surveyId: SurveyId,
    sessionId: SessionId,
    participantId: UserId
  ) {
    return this.authHttp.post(
        environment.apiUrl + 'participant/' + participantId + '/session/' + sessionId + '/survey/' + surveyId,
        null
      );
  }

  public setParticipantSurveyAnswers(
    surveyId: SurveyId,
    sessionId: SessionId,
    participantId: UserId,
    answers: Answer[],
    params?: URLSearchParams
  ) {
    return this.authHttp.post(
        environment.apiUrl + 'participant/' + participantId + '/session/' + sessionId + '/survey/' + surveyId + '/answers',
        { answers: this.prepareAnswersData(answers) },
        { search: params }
      );
  }

  public finishSurvey(
    surveyId: SurveyId,
    sessionId: SessionId,
    participantId: UserId
  ) {
    return this.authHttp.post(
      environment.apiUrl + 'participant/' + participantId + '/session/' + sessionId + '/survey/' + surveyId + '/finish',
      { }
    );
  }

  private prepareAnswersData(answers: Answer[]): any[] {
    let answersData: any[] = [];

    for (const answer of answers) {
      if (answer.hasOwnProperty('content')) {
        answersData.push({
          question: answer.questionId,
          content: answer.getContent()
        });

        continue;
      }

      answersData.push({
        question: answer.questionId,
        answers: answer.getContent()
      });
    }

    return answersData;
  }
}



// WEBPACK FOOTER //
// /dist/src/app/survey/survey.repository.ts